/**
 * claude-rules.ts — The managed "SmartStack Git Safety" block that
 * `ss install` / `ss upgrade` merges into `~/.claude/CLAUDE.md`.
 *
 * The block is delimited by the BEGIN/END markers below so the merge is
 * idempotent and reversible: re-running install replaces the block in place,
 * `ss uninstall` strips it, and anything the user wrote OUTSIDE the markers is
 * never touched. Edit the rule text HERE (versioned), never in ~/.claude.
 *
 * Rationale: the git-guard PreToolUse hook is the HARD enforcement (it blocks
 * the command); this block is the SOFT layer — it tells Claude the rule up
 * front so it proposes instead of attempting. Defense in depth.
 *
 * The rule is TWO-LANE, not a blanket ban: 🟢 committing through `/gitflow commit`
 * and running `/efcore create|apply` on a work branch are autonomous, because the
 * sanctioned CLIs already grade the risk (efcore/cli/lib/migration-policy.ts —
 * 🔴 blocked, 🟡 needs `confirm`, 🟢 passes). 🔴 (push, tag, PR, merge, history
 * rewrite, protected branches, non-local DB) still needs an explicit go-ahead in
 * the current turn. A blanket "never" made Claude beg for every checkpoint and
 * broke the dev flow; the CLIs, not the prose, are what enforce the boundary.
 */

export const CLAUDE_MD_MARKER_BEGIN =
  '<!-- BEGIN SmartStack Git Safety (managed by `ss install` — edit templates source, not here) -->';
export const CLAUDE_MD_MARKER_END = '<!-- END SmartStack Git Safety -->';

/** The rule text placed between the markers (the user's non-negotiable Git/EF rules). */
export const GIT_SAFETY_RULES = `## Règles Git & migrations — deux voies

Le risque est GRADUÉ, pas binaire. Deux voies : 🟢 tu agis seul, 🔴 tu me demandes.
Le doute n'est pas une voie : toute situation non listée en 🟢 est 🔴.

### 🟢 Voie verte — autonome, sans demander

Sur une branche de travail (\`feature/*\`, \`develop\`) :

- **Commiter via le chemin sanctionné** : \`/gitflow commit\` (CLI \`skills/gitflow/cli/commit/\`).
  Tu commites au fil du travail, sans annoncer ni attendre. Un commit local est réversible —
  ce n'est pas un événement.
- **Migrations EF Core via \`/efcore\`** : \`create\` et \`apply\`, tant que le CLI rend un verdict 🟢
  (politique 3 paliers, \`efcore/cli/lib/migration-policy.ts\`). Concrètement : migration additive
  sur \`feature/*\`/\`develop\`, et application sur une base **prouvée locale** : loopback
  (\`localhost\`, \`127.0.0.1\`, \`(localdb)\`, \`.\`), le hostname de la machine (instance nommée
  type \`MONPC\\SQLEXPRESS\`), ou un hôte déclaré dans \`.gitflow/config.json\` →
  \`efcore.localHosts\`. Cette déclaration est une décision UTILISATEUR, faite une fois —
  tu ne l'écris JAMAIS toi-même. Azure SQL (\`*.database.windows.net\`) n'est jamais local.

Le verdict du CLI fait autorité : s'il répond 🟢, tu ne me redemandes pas.

### 🔴 Voie rouge — validation explicite dans le tour courant

Tu PROPOSES en une phrase et tu ATTENDS mon « oui ». Tu n'anticipes jamais ma réponse.

- \`git push\`, \`git tag\`, ouverture de PR, merge, tout artefact distant (GitHub/GitLab)
- création/suppression de branche, worktree, \`reset --hard\`, rebase, toute réécriture
  d'historique, toute ref hors \`refs/heads|remotes|tags\`, tout dépôt/checkpoint parallèle
- TOUTE opération sur \`main\`, \`release/*\`, \`hotfix/*\` — y compris un simple commit
- migration dont le CLI rend 🟡 ou 🔴 : \`Up()\` destructif (Drop/narrowing), \`migrations remove\`,
  \`database update\` sur base distante ou non prouvée locale, squash, revert
- \`npm publish\` — la publication passe par le pipeline Azure, jamais à la main

Un 🟡 n'est pas un refus : tu me montres le diff et tu attends mon \`confirm\`.

### Squash de migrations

Un squash ne supprime JAMAIS une migration déjà présente dans la branche de référence
(la cible du merge : feature→develop ; develop, release, hotfix→main). On ne consolide
que ce qui est propre à la branche.

### Contournement interdit

Les hooks \`PreToolUse\` \`git-guard\` / \`ef-guard\` bloquent le **shell brut** (\`git commit\`,
\`dotnet ef migrations add\` lancés en Bash). C'est voulu : la voie verte passe par les CLIs
\`/gitflow\` et \`/efcore\`, qui appliquent les gardes. Tu ne contournes jamais un guard, tu
n'exportes jamais \`GIT_GUARD_OFF\` / \`EF_GUARD_OFF\` (kill-switch UTILISATEUR uniquement),
tu n'appelles jamais \`git\` / \`dotnet ef\` en brut pour « aller plus vite ».

### En cas de doute

Effet de bord non listé en 🟢 sur l'arbre git, le dépôt distant, la base de données ou le
système de fichiers versionné → tu t'arrêtes et tu demandes. Le silence n'est pas un oui.`;

/** The full managed block (markers + rules), with a trailing newline. */
export function buildClaudeMdBlock(): string {
  return `${CLAUDE_MD_MARKER_BEGIN}\n\n${GIT_SAFETY_RULES}\n\n${CLAUDE_MD_MARKER_END}`;
}
